/**
 * Shared utilities for preview and check routes
 */

// Get API base URL for HTTP calls (convert wss:// to https://)
export function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

// Session storage key for preview context (used by share dialog)
const PREVIEW_CONTEXT_KEY = 'preview_context';

export interface PreviewContext {
  title?: string;
  projectId?: string;
  appId?: string;
  versionId?: string;
  admissionToken?: string;
  agentName?: string;
}

/**
 * Store preview context in sessionStorage before navigating to preview.
 * Called from console components that initiate preview.
 */
export function storePreviewContext(ctx: PreviewContext): void {
  try {
    sessionStorage.setItem(PREVIEW_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // sessionStorage might be unavailable
  }
}

/**
 * Read and clear preview context from sessionStorage.
 */
export function consumePreviewContext(): PreviewContext | null {
  try {
    const stored = sessionStorage.getItem(PREVIEW_CONTEXT_KEY);
    if (stored) {
      sessionStorage.removeItem(PREVIEW_CONTEXT_KEY);
      return JSON.parse(stored);
    }
  } catch {
    // sessionStorage might be unavailable
  }
  return null;
}

export interface ShareResolution {
  admissionToken?: string;
  agentName?: string;
  requiresPasscode: boolean;
  passcodeProtected: boolean;
}

/**
 * Resolve a share ID to an admission token
 */
export async function resolveShare(
  shareId: string,
  passcode?: string
): Promise<ShareResolution> {
  const apiBaseUrl = getApiBaseUrl();
  const res = await fetch(`${apiBaseUrl}/preview/share/${shareId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passcode }),
  });

  const data = await res.json();

  if (res.status === 401) {
    return {
      requiresPasscode: true,
      passcodeProtected: true,
    };
  }

  if (res.status === 410) {
    throw new Error('This preview link has expired.');
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || 'Failed to resolve share');
  }

  return {
    admissionToken: data.admissionToken,
    agentName: data.agentName,
    requiresPasscode: false,
    passcodeProtected: !!passcode,
  };
}
